const CAP56 = {
  "id": 220,
  "title": "Bitcoin como reserva de valor",
  "slug": "bitcoin-como-reserva-de-valor",
  "type": "lesson",
  "category": "bitcoin-basico",
  "level": "basic",
  "preview": "Más allá de ser un medio de pago digital, Bitcoin se ha consolidado como un activo utilizado para proteger ahorro a largo plazo frente a la inflación, la devaluación y los riesgos del sistema financiero tradicional.",
  "sections": [
    {
      "heading": "Introducción: ¿qué significa reserva de valor?",
      "text": [
        "En finanzas, una **reserva de valor** es todo activo que permite almacenar poder adquisitivo a lo largo del tiempo, con la menor pérdida posible.",
        "Históricamente, el oro, algunos bienes inmuebles y, en menor medida, ciertas divisas fuertes han cumplido este papel.",
        "Con la aparición de Bitcoin en 2009, surge la idea de un nuevo tipo de reserva de valor: **puramente digital, escasa por diseño y globalmente accesible**."
      ]
    },
    {
      "heading": "Propiedades clásicas de una reserva de valor",
      "text": [
        "Para que un activo pueda considerarse una buena reserva de valor, suele evaluarse en función de varias propiedades:"
      ],
      "list": [
        "Escasez: no debe poder crearse arbitrariamente en grandes cantidades.",
        "Durabilidad: debe poder conservarse sin degradarse con el tiempo.",
        "Portabilidad: debe ser fácil de transportar o transferir.",
        "Divisibilidad: debe poder fraccionarse en unidades más pequeñas.",
        "Reconocimiento: debe ser comprendido y aceptado por otras personas.",
        "Resistencia a la censura o confiscación: difícil de bloquear, congelar o expropiar."
      ],
      "notes": [
        "Bitcoin fue diseñado explícitamente teniendo en cuenta muchas de estas propiedades, pero aplicadas al mundo digital."
      ]
    },
    {
      "heading": "Por qué algunos consideran a Bitcoin \"oro digital\"",
      "text": [
        "Bitcoin tiene un suministro máximo de 21 millones de monedas, definido en el protocolo desde su origen. Esto contrasta con las monedas fiduciarias, cuyo suministro puede ampliarse mediante políticas monetarias.",
        "Al igual que el oro, Bitcoin no depende de la promesa de ningún gobierno o banco central. Su emisión y verificación se realizan mediante reglas matemáticas y consenso descentralizado.",
        "Esta combinación de **escasez programada**, **emisión predecible** y **validación pública** ha llevado a muchos analistas a describirlo como \"oro digital\"."
      ],
      "list": [
        "Suministro limitado (21 millones).",
        "Emisión decreciente en el tiempo (halvings).",
        "Accesible desde cualquier lugar del mundo.",
        "Puede almacenarse sin intermediarios (autocustodia)."
      ]
    },
    {
      "heading": "Ventajas de Bitcoin como reserva de valor",
      "text": [
        "Bitcoin aporta características que otros activos tradicionales no tienen o no combinan de la misma forma:"
      ],
      "list": [
        "Escasez verificable: cualquiera puede auditar cuántos BTC existen y cuántos quedan por emitirse.",
        "Portabilidad extrema: se pueden mover grandes cantidades de valor en minutos a cualquier parte del mundo.",
        "Divisibilidad: cada bitcoin se divide en 100 millones de satoshis, lo que permite ahorrar e invertir en fracciones muy pequeñas.",
        "Custodia sin terceros: es posible mantener el control total de los fondos mediante claves privadas, sin depender de bancos.",
        "Resistencia a la censura: una vez que una transacción es confirmada, no puede ser alterada ni revertida.",
        "Acceso global: cualquier persona con conexión a internet puede usarlo, sin requisitos de cuenta bancaria."
      ]
    },
    {
      "heading": "Riesgos y limitaciones como reserva de valor",
      "text": [
        "Aunque Bitcoin tiene atributos muy atractivos, también presenta riesgos que deben entenderse antes de usarlo como reserva de valor:"
      ],
      "list": [
        "Alta volatilidad: su precio puede subir o bajar de forma brusca en períodos cortos de tiempo.",
        "Riesgo regulatorio: en algunos países las normas fiscales o financieras pueden ser restrictivas.",
        "Riesgo tecnológico y operativo: mala gestión de claves privadas, estafas, malware o plataformas poco seguras.",
        "Horizonte temporal: se suele considerar más adecuado como reserva de valor a medio y largo plazo, no para necesidades inmediatas.",
        "Curva de aprendizaje: requiere entender conceptos básicos de seguridad digital para evitar pérdidas."
      ],
      "notes": [
        "Usar Bitcoin como reserva de valor no elimina el riesgo; lo traslada de la política monetaria y bancaria tradicional a la seguridad personal y tecnológica."
      ]
    },
    {
      "heading": "Bitcoin frente a la inflación y la devaluación",
      "text": [
        "En muchos países, la inflación erosiona de forma constante el poder adquisitivo de las monedas locales.",
        "Al tener un suministro limitado y una emisión predecible, Bitcoin no puede inflarse discrecionalmente.",
        "Por ello, algunos ahorradores utilizan Bitcoin como complemento a otros activos, buscando proteger parte de su capital frente a la devaluación de su moneda.",
        "Sin embargo, a corto plazo, la volatilidad de Bitcoin puede ser mayor que la inflación, por lo que suele plantearse con una perspectiva de largo plazo."
      ]
    },
    {
      "heading": "Buenas prácticas al usar Bitcoin como reserva de valor",
      "text": [
        "Para quienes deciden destinar una parte de su patrimonio a Bitcoin, es importante seguir ciertas recomendaciones básicas:"
      ],
      "list": [
        "No invertir dinero que puedas necesitar en el corto plazo.",
        "Definir qué porcentaje de tu patrimonio tendrá exposición a Bitcoin, según tu tolerancia al riesgo.",
        "Aprender sobre autocustodia: billeteras frías, frases semilla y copias de seguridad.",
        "Evitar dejar grandes cantidades en plataformas centralizadas de intercambio.",
        "Documentar de forma segura cómo pueden recuperar los fondos tus herederos en caso de fallecimiento.",
        "Estar al tanto de las obligaciones fiscales relacionadas con la compra, venta o tenencia de criptomonedas en tu país."
      ]
    },
    {
      "heading": "Conclusión",
      "text": [
        "Bitcoin ha pasado de ser un experimento tecnológico a convertirse en un activo que millones de personas utilizan como reserva de valor digital.",
        "Su escasez programada, su independencia de gobiernos y bancos centrales, y su naturaleza global lo convierten en una alternativa novedosa frente a las reservas de valor tradicionales.",
        "Al mismo tiempo, su volatilidad, sus riesgos tecnológicos y la necesidad de una buena educación financiera hacen imprescindible aproximarse a él con prudencia y conocimiento.",
        "Más que una solución mágica, Bitcoin puede verse como una herramienta poderosa dentro de una estrategia de ahorro y diversificación bien pensada."
      ]
    }
  ]
}

export default CAP56;
